import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronDown, ChevronUp, Eye, EyeOff, Plus, Radio, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import type {
	MarketingContent,
	MarketingContentFormatType,
	MarketingScreenStateType,
	MarketingSurfaceType
} from '@/api/admin-marketing-content-types'
import { MarketingContentFormat, MarketingScreenState, MarketingSurface } from '@/api/admin-marketing-content-types'
import { useCreateMarketingContent, useReplaceChannelLayout } from '@/api/api-mutations'
import type { Asset } from '@/api/common-types'
import {
	useGetChannelLayout,
	useGetChannelResolvedContent,
	useListMarketingChannels,
	useListMarketingContent
} from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/base/dialog'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { Select } from '@/components/base/select'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { clx } from '@/utils/clx'

import { AssetPicker, checkoutBackgroundBlock, Field } from './-blocks'
import { CheckoutScreenPreview, FeedPreview } from './-preview'

export const Route = createFileRoute('/_authenticated/_general/marketing/layout/')({
	component: FeedLayoutPage,
	staticData: {
		crumb: ({ context: { L } }) => L.Marketing.ContentLayout.Title()
	}
})

// The screen states each surface exposes, in journey order. Dashboard mirror of
// SCREEN_STATES_BY_SURFACE in api/src/business-logic/marketing-resolved-content.ts (the backend is
// the source of truth; this just drives the state rail's order + which surfaces show one). A
// surface with a single state has no rail.
const SCREEN_STATES_BY_SURFACE: Record<MarketingSurfaceType, MarketingScreenStateType[]> = {
	[MarketingSurface.CustomerAppFeed]: [MarketingScreenState.Default],
	[MarketingSurface.CheckoutScreen]: [
		MarketingScreenState.CheckoutIdle,
		MarketingScreenState.CheckoutBagCheck,
		MarketingScreenState.CheckoutCleared
	]
}

const screenStatesForSurface = (surface: MarketingSurfaceType): MarketingScreenStateType[] =>
	SCREEN_STATES_BY_SURFACE[surface] ?? [MarketingScreenState.Default]

// Result states that flash briefly and only ever show their first image (see the editor warning).
const SINGLE_CONTENT_STATES: MarketingScreenStateType[] = [
	MarketingScreenState.CheckoutBagCheck,
	MarketingScreenState.CheckoutCleared
]

// The only content format each surface accepts. Dashboard mirror of FORMAT_BY_SURFACE in
// api/src/business-logic/marketing-content.ts, which rejects anything else on save.
const FORMAT_BY_SURFACE: Record<MarketingSurfaceType, MarketingContentFormatType> = {
	[MarketingSurface.CustomerAppFeed]: MarketingContentFormat.Feed,
	[MarketingSurface.CheckoutScreen]: MarketingContentFormat.CheckoutBackground
}

// Every surface a channel layout can be edited for, with the label shown in the selector.
const useSurfaceLabels = (): Record<MarketingSurfaceType, string> => {
	const { L } = useI18n()
	return {
		[MarketingSurface.CustomerAppFeed]: L.Marketing.Surface.CustomerAppFeed(),
		[MarketingSurface.CheckoutScreen]: L.Marketing.Surface.CheckoutScreen()
	}
}

function FeedLayoutPage() {
	const { L } = useI18n()
	const surfaceLabels = useSurfaceLabels()
	const channelsQuery = useListMarketingChannels()
	const channels = channelsQuery.data ?? []

	const [selectedChannelUuid, setSelectedChannelUuid] = useState<string | null>(null)
	const [selectedSurface, setSelectedSurface] = useState<MarketingSurfaceType>(MarketingSurface.CustomerAppFeed)
	const [selectedScreenState, setSelectedScreenState] = useState<MarketingScreenStateType>(
		MarketingScreenState.Default
	)

	const selectedChannel = channels.find(c => c.uuid === selectedChannelUuid) ?? null

	// The surfaces this channel serves, kept in the enum's canonical order for a stable selector.
	const availableSurfaces = useMemo(
		() => Object.values(MarketingSurface).filter(s => selectedChannel?.surfaces.includes(s)),
		[selectedChannel]
	)

	// Default to the first channel once channels load; keep a valid selection thereafter.
	useEffect(() => {
		if (channels.length === 0) return
		if (!selectedChannelUuid || !channels.some(c => c.uuid === selectedChannelUuid)) {
			setSelectedChannelUuid(channels[0].uuid)
		}
	}, [channels, selectedChannelUuid])

	// Keep the surface selection valid for the chosen channel (a channel only serves some surfaces).
	useEffect(() => {
		if (availableSurfaces.length === 0) return
		if (!availableSurfaces.includes(selectedSurface)) {
			setSelectedSurface(availableSurfaces[0])
		}
	}, [availableSurfaces, selectedSurface])

	// Derive the effective state during render so a surface change takes effect immediately: the
	// selected state may belong to the previous surface for one render (the setter in an effect lags
	// by a frame), which would otherwise mount the editor/preview against an invalid (surface, state)
	// pair. Fall back to the surface's first state in that window.
	const surfaceScreenStates = screenStatesForSurface(selectedSurface)
	const effectiveScreenState = surfaceScreenStates.includes(selectedScreenState)
		? selectedScreenState
		: surfaceScreenStates[0]

	// Reconcile the stored selection with the effective one so later renders and handlers agree.
	useEffect(() => {
		if (selectedScreenState !== effectiveScreenState) {
			setSelectedScreenState(effectiveScreenState)
		}
	}, [selectedScreenState, effectiveScreenState])

	if (channelsQuery.isLoading) {
		return (
			<SingleColumnPage>
				<Container className='flex flex-1 items-center justify-center'>
					<Text className='text-ui-fg-subtle'>{L.General.Loading()}</Text>
				</Container>
			</SingleColumnPage>
		)
	}

	if (channels.length === 0) {
		return (
			<SingleColumnPage>
				<Container className='flex flex-1 flex-col overflow-hidden'>
					<ContainerHeader heading={L.Marketing.ContentLayout.Title()} />
					<div className='flex flex-1 items-center justify-center p-6'>
						<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
							<Radio className='size-10 text-ui-fg-muted' />
							<Heading level='h3'>{L.Marketing.Channel.NoChannels()}</Heading>
							<Text className='max-w-sm text-ui-fg-subtle'>{L.Marketing.ContentLayout.NoChannels()}</Text>
							<Link to='/settings/marketing-channels'>
								<Button>{L.Marketing.ContentLayout.GoToChannels()}</Button>
							</Link>
						</div>
					</div>
				</Container>
			</SingleColumnPage>
		)
	}

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader
					heading={L.Marketing.ContentLayout.Title()}
					rightSide={
						<div className='flex items-center gap-4'>
							{/* Channel (broad) then Surface (a specific view within that channel) - each
							    channel×surface pair has its own independent layout. */}
							<label className='flex items-center gap-1.5 text-xs text-ui-fg-subtle'>
								<span className='hidden sm:inline'>{L.Marketing.ContentLayout.SelectChannel()}</span>
								<Select value={selectedChannelUuid ?? undefined} onValueChange={setSelectedChannelUuid}>
									<Select.Trigger
										className='min-w-[160px] gap-2'
										aria-label={L.Marketing.ContentLayout.SelectChannel()}
									>
										<Select.Value />
									</Select.Trigger>
									<Select.Content>
										{channels.map(c => (
											<Select.Item key={c.uuid} value={c.uuid}>
												{c.name}
											</Select.Item>
										))}
									</Select.Content>
								</Select>
							</label>
							<label className='flex items-center gap-1.5 text-xs text-ui-fg-subtle'>
								<span className='hidden sm:inline'>{L.Marketing.Surface.Label()}</span>
								<Select
									value={selectedSurface}
									onValueChange={value => setSelectedSurface(value as MarketingSurfaceType)}
								>
									<Select.Trigger
										className='min-w-[160px] gap-2'
										aria-label={L.Marketing.Surface.Label()}
									>
										<Select.Value />
									</Select.Trigger>
									<Select.Content>
										{availableSurfaces.map(surface => (
											<Select.Item key={surface} value={surface}>
												{surfaceLabels[surface]}
											</Select.Item>
										))}
									</Select.Content>
								</Select>
							</label>
						</div>
					}
				/>
				{selectedChannelUuid && (
					<StateRail
						channelUuid={selectedChannelUuid}
						surface={selectedSurface}
						selected={effectiveScreenState}
						onSelect={setSelectedScreenState}
					/>
				)}
				{selectedChannelUuid && (
					<ChannelLayoutEditor
						key={`${selectedChannelUuid}:${selectedSurface}:${effectiveScreenState}`}
						channelUuid={selectedChannelUuid}
						surface={selectedSurface}
						screenState={effectiveScreenState}
					/>
				)}
			</Container>
		</SingleColumnPage>
	)
}

// The journey rail for a surface that has more than one screen state (e.g. the checkout screen's
// idle -> bag check / cleared flow). Each item switches the editor + preview below and shows how
// many content items that state's playlist holds. Surfaces with a single state render no rail, so
// the customer-app feed is unchanged.
function StateRail({
	channelUuid,
	surface,
	selected,
	onSelect
}: {
	channelUuid: string
	surface: MarketingSurfaceType
	selected: MarketingScreenStateType
	onSelect: (state: MarketingScreenStateType) => void
}) {
	const states = screenStatesForSurface(surface)
	if (states.length <= 1) return null
	return (
		<div className='flex flex-wrap gap-2 border-b border-ui-border-base px-4 py-3 sm:px-6'>
			{states.map(state => (
				<StateRailItem
					key={state}
					channelUuid={channelUuid}
					surface={surface}
					state={state}
					active={state === selected}
					onSelect={() => onSelect(state)}
				/>
			))}
		</div>
	)
}

// A single state tab. Fetches its own layout so the content count is live and the editor is warm
// (cached) by the time the user switches to it.
function StateRailItem({
	channelUuid,
	surface,
	state,
	active,
	onSelect
}: {
	channelUuid: string
	surface: MarketingSurfaceType
	state: MarketingScreenStateType
	active: boolean
	onSelect: () => void
}) {
	const { L } = useI18n()
	const layoutQuery = useGetChannelLayout({ marketingChannelUuid: channelUuid }, { surface, screenState: state })
	const count = layoutQuery.data?.entries.length ?? 0
	return (
		<button
			type='button'
			onClick={onSelect}
			aria-pressed={active}
			className={clx(
				'flex min-w-[9rem] flex-1 flex-col gap-0.5 rounded-md border px-3 py-2 text-left transition-colors',
				active
					? 'border-ui-border-interactive bg-ui-bg-base shadow-elevation-card-rest'
					: 'border-ui-border-base bg-ui-bg-subtle hover:bg-ui-bg-subtle-hover'
			)}
		>
			<div className='flex items-center justify-between gap-2'>
				<Text className='font-medium'>{L.Marketing.ScreenState.Label(state)}</Text>
				<span
					className={clx(
						'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs',
						active ? 'bg-ui-tag-blue-bg text-ui-tag-blue-text' : 'bg-ui-bg-component text-ui-fg-subtle'
					)}
				>
					{count}
				</span>
			</div>
			<Text className='text-xs text-ui-fg-muted'>{L.Marketing.ScreenState.Hint(state)}</Text>
		</button>
	)
}

function ChannelLayoutEditor({
	channelUuid,
	surface,
	screenState
}: {
	channelUuid: string
	surface: MarketingSurfaceType
	screenState: MarketingScreenStateType
}) {
	const { L } = useI18n()

	const layoutQuery = useGetChannelLayout({ marketingChannelUuid: channelUuid }, { surface, screenState })
	const contentQuery = useListMarketingContent({ limit: 100, offset: 0 })
	const replaceLayout = useReplaceChannelLayout(true, channelUuid)

	const [layoutUuids, setLayoutUuids] = useState<string[]>([])
	const [dirty, setDirty] = useState(false)
	const [previewOpen, setPreviewOpen] = useState(true)

	const dirtyRef = useRef(false)
	const setDirtyBoth = (v: boolean) => {
		dirtyRef.current = v
		setDirty(v)
	}

	// Seed from the saved layout; don't clobber in-progress edits on background refetches.
	useEffect(() => {
		const layout = layoutQuery.data
		if (!layout) return
		if (dirtyRef.current) return
		setLayoutUuids(layout.entries.map(e => e.content.uuid))
		setDirtyBoth(false)
	}, [layoutQuery.data])

	const feedQuery = useGetChannelResolvedContent(
		{ marketingChannelUuid: channelUuid },
		{ surface, screenState },
		{ enabled: previewOpen }
	)

	const contentByUuid = useMemo(() => {
		const map = new Map<string, MarketingContent>()
		for (const c of contentQuery.data?.content ?? []) map.set(c.uuid, c)
		return map
	}, [contentQuery.data])

	const isCheckout = surface === MarketingSurface.CheckoutScreen
	const expectedFormat = FORMAT_BY_SURFACE[surface]

	// Layout entries carry their content's format too, so placed content outside the first page of
	// the content list can still be checked.
	const formatByUuid = useMemo(() => {
		const map = new Map<string, MarketingContentFormatType>()
		for (const e of layoutQuery.data?.entries ?? []) map.set(e.content.uuid, e.content.format)
		for (const c of contentQuery.data?.content ?? []) map.set(c.uuid, c.format)
		return map
	}, [layoutQuery.data, contentQuery.data])

	// Content placed before formats existed (or on the wrong surface) is flagged so the manager can
	// swap it out; the server refuses to save a layout that still holds it.
	const isMisplaced = (uuid: string) => {
		const format = formatByUuid.get(uuid)
		return format !== undefined && format !== expectedFormat
	}
	const hasMisplaced = layoutUuids.some(isMisplaced)

	const contentOfFormat = useMemo(
		() => (contentQuery.data?.content ?? []).filter(c => c.format === expectedFormat),
		[contentQuery.data, expectedFormat]
	)
	const available = useMemo(
		() => contentOfFormat.filter(c => !layoutUuids.includes(c.uuid)),
		[contentOfFormat, layoutUuids]
	)

	const [newBackgroundOpen, setNewBackgroundOpen] = useState(false)

	const move = (index: number, direction: -1 | 1) => {
		const next = index + direction
		if (next < 0 || next >= layoutUuids.length) return
		const copy = [...layoutUuids]
		;[copy[index], copy[next]] = [copy[next], copy[index]]
		setLayoutUuids(copy)
		setDirtyBoth(true)
	}

	const add = (uuid: string) => {
		setLayoutUuids([...layoutUuids, uuid])
		setDirtyBoth(true)
	}

	const remove = (uuid: string) => {
		setLayoutUuids(layoutUuids.filter(u => u !== uuid))
		setDirtyBoth(true)
	}

	const handleSave = async () => {
		try {
			const result = await replaceLayout.mutateAsync({
				path: { marketingChannelUuid: channelUuid },
				query: { surface, screenState },
				body: { contentUuids: layoutUuids }
			})
			if (!result.isSuccess) {
				toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
				return
			}
			setDirtyBoth(false)
			toast.success(L.Marketing.ContentLayout.Saved())
			if (previewOpen) void feedQuery.refetch()
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	if (layoutQuery.isLoading) {
		return (
			<div className='flex flex-1 items-center justify-center p-6'>
				<Text className='text-ui-fg-subtle'>{L.General.Loading()}</Text>
			</div>
		)
	}

	return (
		<div className='flex flex-1 gap-6 overflow-hidden'>
			<div className='flex-1 overflow-y-auto p-4 sm:p-6'>
				<div className='flex flex-col gap-6'>
					<div className='flex items-start justify-between gap-4'>
						<Text className='max-w-2xl text-xs text-ui-fg-muted'>
							{isCheckout ? L.Marketing.ContentLayout.CheckoutHelp() : L.Marketing.ContentLayout.Help()}
						</Text>
						<IconButton
							tooltipText={L.Marketing.ContentLayout.PreviewTitle()}
							onClick={() => setPreviewOpen(o => !o)}
							variant='transparent'
							className='hidden lg:inline-flex'
						>
							{previewOpen ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
						</IconButton>
					</div>

					<section className='flex flex-col gap-2'>
						<Heading level='h2'>{L.Marketing.ContentLayout.InLayoutTitle()}</Heading>
						{SINGLE_CONTENT_STATES.includes(screenState) && layoutUuids.length > 1 && (
							<Text className='text-xs text-ui-tag-orange-text'>
								{L.Marketing.ContentLayout.SingleContentWarning()}
							</Text>
						)}
						{layoutUuids.length === 0 ? (
							<div className='rounded-md border border-dashed border-ui-border-base p-8 text-center'>
								<Text className='text-sm text-ui-fg-subtle'>{L.Marketing.ContentLayout.Empty()}</Text>
							</div>
						) : (
							<div className='flex flex-col gap-1.5'>
								{layoutUuids.map((uuid, index) => {
									const content = contentByUuid.get(uuid)
									const misplaced = isMisplaced(uuid)
									return (
										<div
											key={uuid}
											className={clx(
												'flex items-center gap-3 rounded-md border bg-ui-bg-base px-3 py-2.5',
												misplaced ? 'border-ui-tag-orange-border' : 'border-ui-border-base'
											)}
										>
											<div className='flex flex-col'>
												<button
													type='button'
													className='text-ui-fg-muted hover:text-ui-fg-base disabled:opacity-30'
													disabled={index === 0}
													onClick={() => move(index, -1)}
													aria-label={L.Marketing.Campaign.Action.MoveUp()}
												>
													<ChevronUp className='size-3' />
												</button>
												<button
													type='button'
													className='text-ui-fg-muted hover:text-ui-fg-base disabled:opacity-30'
													disabled={index === layoutUuids.length - 1}
													onClick={() => move(index, 1)}
													aria-label={L.Marketing.Campaign.Action.MoveDown()}
												>
													<ChevronDown className='size-3' />
												</button>
											</div>
											<div className='flex flex-1 flex-col'>
												<Link
													to='/marketing/content/$contentUuid'
													params={{ contentUuid: uuid }}
													className='font-medium hover:underline'
												>
													{content?.name ?? uuid}
												</Link>
												{content && (
													<Text className='whitespace-nowrap text-xs text-ui-fg-subtle'>
														{L.Marketing.Campaign.StatusEnum(content.status)}
														{' · '}
														{L.Marketing.Content.BlockCount({ count: content.blockCount })}
													</Text>
												)}
												{misplaced && (
													<Text className='text-xs text-ui-tag-orange-text'>
														{isCheckout
															? L.Marketing.ContentLayout.WrongFormatOnCheckout()
															: L.Marketing.ContentLayout.WrongFormatOnFeed()}
													</Text>
												)}
											</div>
											<IconButton
												variant='transparent'
												size='small'
												tooltipText={L.Marketing.ContentLayout.Remove()}
												onClick={() => remove(uuid)}
											>
												<Trash2 className='size-4' />
											</IconButton>
										</div>
									)
								})}
							</div>
						)}
						<div className='flex items-center justify-end gap-3'>
							{hasMisplaced && (
								<Text className='text-xs text-ui-tag-orange-text'>
									{L.Marketing.ContentLayout.RemoveFlaggedToSave()}
								</Text>
							)}
							<Button onClick={handleSave} disabled={!dirty || hasMisplaced || replaceLayout.isPending}>
								{L.Marketing.ContentLayout.Save()}
							</Button>
						</div>
					</section>

					<section className='flex flex-col gap-2'>
						<div className='flex items-center justify-between gap-4'>
							<Heading level='h2'>
								{isCheckout
									? L.Marketing.ContentLayout.AvailableBackgroundsTitle()
									: L.Marketing.ContentLayout.AvailableTitle()}
							</Heading>
							{isCheckout && (
								<Button variant='secondary' size='small' onClick={() => setNewBackgroundOpen(true)}>
									<Plus className='mr-1 size-3.5' />
									{L.Marketing.ContentLayout.NewBackground()}
								</Button>
							)}
						</div>
						{available.length === 0 ? (
							<Text className='text-sm text-ui-fg-subtle'>
								{isCheckout && contentOfFormat.length === 0
									? L.Marketing.ContentLayout.NoBackgrounds()
									: L.Marketing.ContentLayout.NoAvailable()}
							</Text>
						) : (
							<div className='flex flex-col gap-1.5'>
								{available.map(content => (
									<div
										key={content.uuid}
										className='flex items-center gap-3 rounded-md border border-ui-border-base bg-ui-bg-subtle px-3 py-2.5'
									>
										<div className='flex flex-1 flex-col'>
											<Link
												to='/marketing/content/$contentUuid'
												params={{ contentUuid: content.uuid }}
												className='font-medium hover:underline'
											>
												{content.name}
											</Link>
											<div className='flex items-center gap-1.5 whitespace-nowrap text-xs leading-none text-ui-fg-subtle'>
												<span
													className={clx(
														'size-2 shrink-0 rounded-sm',
														content.status === 'PUBLISHED'
															? 'bg-ui-tag-green-icon'
															: 'bg-ui-tag-neutral-icon'
													)}
												/>
												<span>{L.Marketing.Campaign.StatusEnum(content.status)}</span>
												<span aria-hidden>·</span>
												<span>
													{L.Marketing.Content.BlockCount({ count: content.blockCount })}
												</span>
											</div>
										</div>
										<Button variant='secondary' size='small' onClick={() => add(content.uuid)}>
											<Plus className='mr-1 size-3.5' />
											{L.Marketing.ContentLayout.Add()}
										</Button>
									</div>
								))}
							</div>
						)}
					</section>
				</div>
				{isCheckout && (
					<NewBackgroundDialog
						open={newBackgroundOpen}
						onOpenChange={setNewBackgroundOpen}
						onCreated={uuid => {
							add(uuid)
							toast.success(L.Marketing.ContentLayout.NewBackgroundCreated())
						}}
					/>
				)}
			</div>
			{previewOpen && (
				<div className='hidden w-[360px] shrink-0 overflow-y-auto border-l border-ui-border-base bg-ui-bg-subtle p-4 [-ms-overflow-style:none] [scrollbar-width:none] lg:block [&::-webkit-scrollbar]:hidden'>
					<Text className='mb-2 text-center text-xs font-medium uppercase tracking-wide text-ui-fg-muted'>
						{L.Marketing.ContentLayout.PreviewTitle()}
					</Text>
					{feedQuery.isLoading ? (
						<Text className='text-center text-xs text-ui-fg-subtle'>{L.General.Loading()}</Text>
					) : (
						<>
							{surface === MarketingSurface.CheckoutScreen ? (
								<CheckoutScreenPreview items={feedQuery.data ?? []} screenState={screenState} />
							) : (
								<FeedPreview items={feedQuery.data ?? []} />
							)}
							<Text className='mt-2 text-center text-[11px] text-ui-fg-muted'>
								{surface === MarketingSurface.CheckoutScreen
									? L.Marketing.Layout.CheckoutPreviewHint()
									: L.Marketing.Layout.LivePreviewHint()}
							</Text>
						</>
					)}
				</div>
			)}
		</div>
	)
}

// Creates a published checkout background from one image and hands its uuid back so the editor can
// add it to the playlist, letting the manager stay on the checkout screen they're arranging.
function NewBackgroundDialog({
	open,
	onOpenChange,
	onCreated
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onCreated: (contentUuid: string) => void
}) {
	const { L } = useI18n()
	const createContent = useCreateMarketingContent()
	const [name, setName] = useState('')
	const [asset, setAsset] = useState<Asset | undefined>()
	const [isCreating, setIsCreating] = useState(false)

	const handleOpenChange = (next: boolean) => {
		if (!next) {
			setName('')
			setAsset(undefined)
		}
		onOpenChange(next)
	}

	const handleCreate = async () => {
		if (!asset) return
		const block = checkoutBackgroundBlock(asset)
		setIsCreating(true)
		try {
			const result = await createContent({
				body: {
					name: name.trim() || L.Marketing.ContentLayout.NewBackground(),
					format: MarketingContentFormat.CheckoutBackground,
					status: 'PUBLISHED',
					blocks: [{ type: 'BANNER', data: block.data }]
				}
			})
			if (!result.isSuccess) {
				toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
				return
			}
			onCreated(result.response.uuid)
			handleOpenChange(false)
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		} finally {
			setIsCreating(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className='border-ui-border-base bg-ui-bg-base sm:max-w-lg'>
				<DialogHeader>
					<DialogTitle>{L.Marketing.ContentLayout.NewBackgroundTitle()}</DialogTitle>
					<DialogDescription>{L.Marketing.ContentLayout.NewBackgroundDescription()}</DialogDescription>
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<Field label={L.Marketing.Campaign.Field.Name()}>
						<Input
							value={name}
							placeholder={L.Marketing.ContentLayout.NewBackground()}
							onChange={e => setName(e.target.value)}
						/>
					</Field>
					<Field label={L.Marketing.Content.BackgroundImage()}>
						<AssetPicker selectedAsset={asset} onSelect={setAsset} />
					</Field>
					<Text className='text-xs text-ui-fg-muted'>{L.Marketing.Content.BackgroundHelp()}</Text>
				</div>
				<DialogFooter>
					<Button variant='secondary' onClick={() => handleOpenChange(false)}>
						{L.General.Cancel()}
					</Button>
					<Button onClick={handleCreate} disabled={!asset || isCreating}>
						{L.Marketing.ContentLayout.NewBackgroundCreate()}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
